import { Audio, AVPlaybackSource, AVPlaybackStatus } from 'expo-av';
import { useCallback, useEffect, useRef } from 'react';

export function useLoadSoundRetry({
  source,
  createOptions,
  updateStatus,
}: {
  source?: AVPlaybackSource;
  createOptions?: string;
  updateStatus: (status: AVPlaybackStatus) => void;
}) {
  const retryTimes = useRef(0);
  const timerIdRef = useRef<number | NodeJS.Timeout>();

  const wait = (ms: number) =>
    new Promise((resolve) => (timerIdRef.current = setTimeout(resolve, ms)));

  useEffect(() => {
    return timerIdRef.current
      ? () => {
          clearTimeout(timerIdRef.current as number);
        }
      : undefined;
  }, []);

  const loadAudioRetry = useCallback(async () => {
    if (!source) {
      return null;
    }
    retryTimes.current = retryTimes.current + 1;
    const raceValue = await Promise.race([
      _createAsync(source, createOptions, updateStatus),
      wait(5000),
    ]);

    if (!!raceValue && raceValue instanceof Audio.Sound) {
      clearTimeout(timerIdRef.current as number);
      return raceValue;
    }

    if (retryTimes.current > 10) {
      throw new Error('retried 10 times');
    }
    loadAudioRetry();
    return null;
  }, [createOptions, source, updateStatus]);

  return { loadAudioRetry };
}

const _createAsync = async (
  source: AVPlaybackSource,
  createOptions?: string,
  updateStatus?: (status: AVPlaybackStatus) => void
) => {
  const { sound: soundObject } = await Audio.Sound.createAsync(
    source,
    {
      ...(createOptions ? JSON.parse(createOptions) : null),
      androidImplementation: 'MediaPlayer',
    },
    updateStatus
  );

  const isLoaded = (await soundObject.getStatusAsync()).isLoaded;
  return isLoaded ? soundObject : null;
};
